import { useAppStore } from '@/stores/app'
import { getTelegramWebApp } from '@/telegram/getTelegramWebApp'
import { parseStartParam } from '@/telegram/parseStartParam'
import { routeNeedsBack } from '@/telegram/routeNeedsBack'

export async function bootstrapTelegram({ i18n }) {
  const tg = getTelegramWebApp()
  if (!tg?.initData) {
    return { isTgApp: false }
  }

  tg.ready()
  tg.expand()
  tg.enableClosingConfirmation?.()
  tg.disableVerticalSwipes?.()

  function setCssPxVar(name, value) {
    const px = Number(value)
    if (Number.isFinite(px) && px >= 0) {
      document.documentElement.style.setProperty(name, `${px}px`)
    }
  }

  function syncViewportCssVars() {
    const stableHeight = Number(tg.viewportStableHeight)
    if (Number.isFinite(stableHeight) && stableHeight > 0) {
      document.documentElement.style.setProperty('--app-viewport-height', `${stableHeight}px`)
      document.documentElement.style.setProperty('--tg-viewport-stable-height', `${stableHeight}px`)
    }

    const safeTop = Number(tg.safeAreaInset?.top)
    const safeBottom = Number(tg.safeAreaInset?.bottom)
    const contentSafeTop = Number(tg.contentSafeAreaInset?.top)
    const contentSafeBottom = Number(tg.contentSafeAreaInset?.bottom)

    setCssPxVar('--tg-safe-area-top', safeTop)
    setCssPxVar('--tg-safe-area-bottom', safeBottom)
    setCssPxVar('--tg-content-safe-area-top', contentSafeTop)
    setCssPxVar('--tg-content-safe-area-bottom', contentSafeBottom)

    const headerInset =
      (Number.isFinite(safeTop) && safeTop > 0 ? safeTop : 0) +
      (Number.isFinite(contentSafeTop) && contentSafeTop > 0 ? contentSafeTop : 0)
    setCssPxVar('--tg-header-inset-top', headerInset)
  }

  syncViewportCssVars()
  tg.onEvent('viewportChanged', syncViewportCssVars)
  tg.onEvent('safeAreaChanged', syncViewportCssVars)
  tg.onEvent('contentSafeAreaChanged', syncViewportCssVars)
  tg.onEvent('fullscreenChanged', syncViewportCssVars)

  const appStore = useAppStore()
  appStore.setTgApp(true)

  const lang = tg.initDataUnsafe?.user?.language_code
  if (lang === 'ru' || lang === 'en') {
    i18n.global.locale.value = lang
    appStore.setLang(lang)
  }

  const startParam = tg.initDataUnsafe?.start_param
  const refCode = parseStartParam(startParam, 'ref')
  const billHash = parseStartParam(startParam, 'bill')
  const dishId = parseStartParam(startParam, 'dish')

  return {
    isTgApp: true,
    initData: tg.initData,
    refCode,
    billHash,
    dishId,
    tg,
  }
}

export async function bindTelegramRouter(router, tgContext) {
  if (!tgContext?.isTgApp || !tgContext.tg) {
    return
  }

  const { tg, billHash, dishId } = tgContext

  if (billHash) {
    await router.replace({ path: `/bill/${billHash}` }).catch(() => {})
  } else if (dishId) {
    await router.replace({ name: 'client-dish', params: { id: dishId } }).catch(() => {})
  }

  const backButton = tg.BackButton

  backButton.onClick(() => {
    if (window.history.state?.back) {
      window.history.back()
      return
    }
    router.push({ name: 'client-home' })
  })

  const syncBackButton = (route) => {
    if (routeNeedsBack(route)) {
      backButton.show()
    } else {
      backButton.hide()
    }
  }

  router.afterEach((to) => syncBackButton(to))
  syncBackButton(router.currentRoute.value)
}
